/**
 * How current is this data?
 *
 * Two different questions, deliberately kept apart:
 *
 *   formatLastSynced   — when we last successfully talked to the broker.
 *                        Answers "is syncing still working?"
 *   formatThroughDate  — the newest data point we actually hold.
 *                        Answers "how current is what I am looking at?"
 *
 * Conflating them is how a dead connection looks healthy: a page can honestly
 * say "synced 2 minutes ago" while the newest trade it holds is four days old,
 * because the sync ran and returned nothing.
 */

/** Relative age — reads faster than a timestamp for "is this stale?". */
export function formatLastSynced(iso) {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 'unknown';

  const mins = Math.floor((Date.now() - then) / 60000);
  // A clock skew between server and browser can put a timestamp slightly in
  // the future; "in -2 min" would be worse than rounding to now.
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** "Aug 15, 2026" — absolute, because a data boundary is a specific day. */
export function formatThroughDate(value) {
  if (!value) return null;
  const raw = String(value);
  // A bare YYYY-MM-DD parses as UTC midnight, which renders as the PREVIOUS
  // day for anyone west of Greenwich. Anchoring it to local time keeps the
  // label matching the date on the chart axis.
  const date = new Date(raw.length <= 10 ? `${raw}T00:00:00` : raw);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
